import { __ } from '@wordpress/i18n';
import { decodeEntities } from '@wordpress/html-entities';
import debounce from 'lodash/debounce';
import { carouselInit } from '../../../templates/list/carousel/script';

// Update the TRANSLATIONS object to use localized strings from PHP
const TRANSLATIONS = {
    noPosts: hoyryListing?.translations?.noPosts || __('No posts found.', 'hoyry-listing-block'),
    errorLoading: hoyryListing?.translations?.errorLoading || __('Error loading posts.', 'hoyry-listing-block'),
    loadMore: hoyryListing?.translations?.loadMore || __('Load More', 'hoyry-listing-block'),
    loading: hoyryListing?.translations?.loading || __('Loading...', 'hoyry-listing-block'),
    close: hoyryListing?.translations?.close || __('Close', 'hoyry-listing-block'),
};

/**
 * Handles query block functionality including filtering, load more, and template rendering
 * Implements Singleton pattern to ensure only one instance exists
 */
class QueryBlockHandler {
    
    constructor() {
        
        if (QueryBlockHandler.instance) {
            return QueryBlockHandler.instance;
        }
        
        if (document.querySelectorAll('.hoyry-listing-item[data-open-in-popup="true"]').length > 0 || document.querySelectorAll('.hoyry-featured-item[data-open-in-popup="true"]').length > 0) {
            this.initializeModal();
        }
        
        this.isLoading = false;
        this.boundHandleLoadMore = this.handleLoadMore.bind(this);
        this.templateCache = new Map();
        
        QueryBlockHandler.instance = this;
        
    }

    /**
     * Initializes blocks with their templates and initial data
     */
    async initializeBlocks() {
        
        let blocks = document.querySelectorAll('.wp-block-hoyry-listing');

        for (const block of blocks) {
            try {
                // Get template type from the query data
                let template = 'grid'; // default to grid
                const listingElement = block.querySelector('[data-listing-element="wrapper"]');
                
                if (listingElement) {
                    try {
                        const queryData = JSON.parse(listingElement.dataset.query);
                        template = queryData.template || 'grid';
                    } catch (e) {
                        console.error('Error parsing query data:', e);
                    }
                }
                                
                // Prefetch template
                await this.prefetchTemplate(template);
            } catch (error) {
                console.error('Error initializing block:', error);
            }
        }
    }
    
    /**
     * Handles filter changes and updates post display
     */
    async handleFilterChange(block, filterData) {
        if (this.isLoading) return;

        // Get the listing block ID
        const listingId = block.dataset.listingId;
        
        if (!listingId) {
            console.error('Listing ID not found in handleFilterChange');
            return;
        }

        // Update URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        
        // Check if this is a reset action (all filters empty)
        const isReset = Object.keys(filterData.taxonomies).length === 0 && !filterData.search;
        
        // If it's a reset, clear all filters for this listing
        if (isReset) {
            Array.from(urlParams.keys()).forEach(key => {
                if (key.startsWith(`fid${listingId}_`)) {
                    urlParams.delete(key);
                }
            });
        } else {
            // First, collect all existing filter parameters for this listing
            const existingFilters = {
                taxonomies: {},
                search: ''
            };
            
            // Get existing taxonomy filters from URL
            Array.from(urlParams.keys()).forEach(key => {
                if (key.startsWith(`fid${listingId}_`) && !key.endsWith('_search')) {
                    const taxonomy = key.replace(`fid${listingId}_`, '');
                    const termIds = urlParams.get(key).split(',').map(id => parseInt(id, 10));
                    existingFilters.taxonomies[taxonomy] = termIds;
                }
            });
            
            // Get existing search filter from URL
            const searchKey = `fid${listingId}_search`;
            if (urlParams.has(searchKey)) {
                existingFilters.search = urlParams.get(searchKey);
            }
            
            // Merge existing filters with new filter data
            // For taxonomies, replace specific taxonomies that are in the new filterData
            const mergedFilters = {
                taxonomies: { ...existingFilters.taxonomies },
                search: filterData.search !== undefined ? filterData.search : existingFilters.search
            };
            
            // Update specific taxonomies from the new filterData
            if (filterData.taxonomies) {
                Object.entries(filterData.taxonomies).forEach(([taxonomy, termIds]) => {
                    if (termIds && termIds.length > 0) {
                        mergedFilters.taxonomies[taxonomy] = termIds;
                    } else {
                        delete mergedFilters.taxonomies[taxonomy];
                    }
                });
            }
            
            // Now clear all existing filter params for this listing
            Array.from(urlParams.keys()).forEach(key => {
                if (key.startsWith(`fid${listingId}_`)) {
                    urlParams.delete(key);
                }
            });
            
            // Add merged taxonomy filters to URL
            Object.entries(mergedFilters.taxonomies).forEach(([taxonomy, termIds]) => {
                if (termIds && termIds.length > 0) {
                    urlParams.set(`fid${listingId}_${taxonomy}`, termIds.join(','));
                }
            });
            
            // Add search filter to URL
            if (mergedFilters.search) {
                urlParams.set(`fid${listingId}_search`, mergedFilters.search);
            }
            
            // Use mergedFilters for the rest of the function
            filterData = mergedFilters;
        }
        
        // Update URL without reloading the page
        const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        window.history.pushState({}, '', newUrl);
        
        const blockContainer = block.closest('.wp-block-hoyry-listing-content');
        const postsContainer = blockContainer?.querySelector('.hoyry-listing-items');
        const loadMoreContainer = blockContainer?.querySelector('.hoyry-listing-load-more-container');
        const button = loadMoreContainer?.querySelector('.hoyry-listing-load-more');
        const buttonTextContainer = button?.querySelector('.hoyry-listing-load-more-text');
        
        const originalText = TRANSLATIONS.loadMore;
        
        if (!blockContainer || !postsContainer) {
            return;
        }

        let queryData;
        try {
            queryData = JSON.parse(blockContainer.dataset.query);
        } catch (error) {
            console.error('Failed to parse query data:', error);
            return;
        }

        try {
            this.isLoading = true;
            postsContainer.style.opacity = '0.5';
            if (buttonTextContainer) {
                buttonTextContainer.textContent = TRANSLATIONS.loading;
            }

            // Check if any filters are active
            const hasActiveFilters = Object.keys(filterData.taxonomies).length > 0 || filterData.search;

            // Store original values if not already stored
            if (!queryData.originalState) {
                queryData.originalState = {
                    taxonomyTerms: queryData.taxonomyTerms || {},
                    perPage: queryData.perPage || 10
                };
            }

            // Get the perPage value
            const perPage = queryData.originalState.perPage;

            // Get current language from window.wpData or fallback
            let currentLang = window.wpData?.lang || '';
            if (currentLang === '') {
                // Try to get language from body class (locale-xx)
                const bodyClasses = document.body.className.split(' ');
                const localeClass = bodyClasses.find(className => className.startsWith('locale-'));
                
                if (localeClass) {
                    // Extract language code from locale-xx format
                    currentLang = localeClass.replace('locale-', '');
                }
            }

            // Construct the updated query data

            const updatedQueryData = {
                ...queryData,
                query: {
                    perPage: perPage,
                    page: 1,
                    postType: queryData.postType || 'post',
                    orderBy: queryData.orderBy || 'date',
                    order: queryData.order || 'desc',
                    taxonomies: filterData.taxonomies,
                    taxonomyTerms: hasActiveFilters ? filterData.taxonomies : (queryData.originalState.taxonomyTerms || {}),
                    search: filterData.search || '',
                    exclude: queryData.query?.exclude || queryData.exclude || []
                },
                template: queryData.template || 'grid',
                taxonomyTerms: hasActiveFilters ? filterData.taxonomies : queryData.originalState.taxonomyTerms,
                currentLang: currentLang,
                search: filterData.search || '',
                exclude: queryData.exclude || []
            };

            const blockId = block.closest('[class*="wp-block-"]')?.id || '';

            // Update the query data in the container's dataset
            blockContainer.dataset.query = JSON.stringify(updatedQueryData);

            // Build request data
            const requestData = {
                attributes: updatedQueryData,
                filterData: {
                    ...filterData,
                    template: queryData.template || 'grid',
                    listingId: listingId
                },
                blockId: blockId
            };

            // Make the request
            const response = await fetch('/wp-json/hoyry-listing/v2/filter', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': hoyryListing.nonce,
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin',
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.data?.length > 0) {
                // Get template from cache
                const templateName = queryData.template || 'grid';
                const template = this.templateCache.get(templateName);
                
                if (!template) {
                    console.error('Template not found:', templateName);
                    return;
                }
        
                // Create container with correct template class
                const container = document.createElement('div');
                container.className = `hoyry-listing-is-layout-${templateName} hoyry-listing-items`;
                
                const postsHtml = data.data.map(post => {
                    const postElement = document.createElement('div');
                    postElement.innerHTML = template.postContainer;
                    const postNode = postElement.firstElementChild;
                    post.template = templateName;
                    this.updatePostContent(postNode, post, template.placeholderImage);
                    return postNode.outerHTML;
                }).join('');
        
                container.innerHTML = postsHtml;
                postsContainer.innerHTML = container.innerHTML;
        
                // Update load more button based on max_pages
                const loadMoreContainer = blockContainer.querySelector('.hoyry-listing-load-more-container');
                const button = loadMoreContainer?.querySelector('.hoyry-listing-load-more');
                
                if (loadMoreContainer && button) {
                    // Reset button state
                    button.dataset.page = '1';
                    button.dataset.maxPages = data.max_pages;
                    
                    if (data.max_pages > 1) {
                        button.disabled = false;
                        loadMoreContainer.style.display = ''; // Remove inline style to show
                        const buttonText = button.querySelector('.hoyry-listing-load-more-text');
                        if (buttonText) {
                            buttonText.textContent = TRANSLATIONS.loadMore;
                        }
                    } else {
                        button.disabled = true;
                        loadMoreContainer.style.display = 'none';
                    }
                } else if (data.max_pages > 1) {
                    // If load more button doesn't exist but we need one, create it
                    this.createLoadMoreButton(postsContainer, data.max_pages);
                }
            } else {
                postsContainer.innerHTML = '<div class="hoyry-listing-no-posts">' + TRANSLATIONS.noPosts + '</div>';
                // Hide and disable load more button
                const loadMoreContainer = blockContainer.querySelector('.hoyry-listing-load-more-container');
                if (loadMoreContainer) {
                    loadMoreContainer.style.display = 'none';
                }
            }

        } catch (error) {
            console.error('Filter error:', error);
            postsContainer.innerHTML = '<div class="hoyry-listing-error">' + TRANSLATIONS.errorLoading + '</div>';
        } finally {
            this.isLoading = false;
            postsContainer.style.opacity = '1';
            if (buttonTextContainer && button && !button.disabled) {
                buttonTextContainer.textContent = originalText;
            }
            
            // Re-initialize load more event listeners
            this.initializeLoadMore();
        }
    }

    /**
     * Updates or creates load more button based on pagination state
     */
    updateLoadMoreButton(block, maxPages) {
        let loadMoreContainer = block.nextElementSibling;
        const hasLoadMoreContainer = loadMoreContainer?.classList.contains('hoyry-listing-load-more-container');

        if (maxPages > 1) {
            if (!hasLoadMoreContainer) {
                this.createLoadMoreButton(block, maxPages);
            } else {
                this.updateExistingLoadMoreButton(loadMoreContainer, maxPages);
            }
        } else if (hasLoadMoreContainer) {
            loadMoreContainer.remove();
        }
    }

    /**
     * Creates new load more button
     */
    createLoadMoreButton(postsContainer, maxPages) {
        const loadMoreContainer = document.createElement('div');
        loadMoreContainer.className = 'hoyry-listing-load-more-container';
        
        const button = document.createElement('button');
        button.className = 'hoyry-listing-load-more';
        button.dataset.page = '1';
        button.dataset.maxPages = maxPages;
        
        const buttonText = document.createElement('span');
        buttonText.className = 'hoyry-listing-load-more-text';
        buttonText.textContent = TRANSLATIONS.loadMore;
        
        button.appendChild(buttonText);
        
        loadMoreContainer.appendChild(button);
        postsContainer.appendChild(loadMoreContainer);
        
        button.addEventListener('click', this.boundHandleLoadMore);
    }

    /**
     * Updates existing load more button
     */
    updateExistingLoadMoreButton(container, maxPages) {
        const button = container.querySelector('.hoyry-listing-load-more');
        if (button) {
            button.dataset.page = '1';
            button.dataset.maxPages = maxPages;
            button.disabled = false;
            button.textContent = TRANSLATIONS.loadMore;
            button.removeEventListener('click', this.boundHandleLoadMore);
            button.addEventListener('click', this.boundHandleLoadMore);
        }
    }
    
    initializeFilters() {
        document.querySelectorAll('.wp-block-hoyry-listing').forEach((wrapper) => {
            const contentBlock = wrapper.querySelector('.wp-block-hoyry-listing-content');
            const filterBlocks = wrapper.querySelectorAll('.wp-block-hoyry-listing-filter');

            if ( filterBlocks.length === 0 || !contentBlock || !contentBlock.dataset || !contentBlock.dataset.listingId || contentBlock.dataset.templateSupportsFilters === '0' ) {
                return;
            }

            // Create empty filter data object for this listing
            const filterData = {
                taxonomies: {},
                search: ''
            };

            // Process all filter blocks to initialize the state
            filterBlocks.forEach(filterBlock => {
                // Find all taxonomy filter elements in this filter block
                const filterElements = filterBlock.querySelectorAll('[data-listing-element="filter"][data-listing-filter-type="taxonomy"]');
                
                filterElements.forEach(element => {
                    const taxonomy = element.dataset.listingTaxonomy;
                    
                    // Skip if no taxonomy is specified
                    if (!taxonomy) return;
                    
                    // Initialize taxonomy array if it doesn't exist
                    if (!filterData.taxonomies[taxonomy]) {
                        filterData.taxonomies[taxonomy] = [];
                    }

                    // Check if element is already active (PHP may have set this)
                    if (element.tagName === 'SELECT') {
                        const termId = parseInt(element.value, 10);
                        if (!isNaN(termId) && termId !== 0) {
                            // If we already have values for this taxonomy, replace them
                            filterData.taxonomies[taxonomy] = [termId];
                        }
                    } else if ((element.tagName === 'BUTTON' || element.tagName === 'A') && element.classList.contains('is-active')) {
                        const termId = parseInt(element.dataset.listingTermId, 10);
                        if (!isNaN(termId) && termId !== 0 && termId !== 'all') {
                            if (!filterData.taxonomies[taxonomy].includes(termId)) {
                                filterData.taxonomies[taxonomy].push(termId);
                            }
                        }
                    }
                });
            });

            // Clean up empty taxonomy arrays
            Object.keys(filterData.taxonomies).forEach(key => {
                if (filterData.taxonomies[key].length === 0) {
                    delete filterData.taxonomies[key];
                }
            });

            // Store current state for each listing
            // Create a closure variable for this particular listing
            const listingFilterData = { ...filterData };

            // Add event listeners to all filter blocks
            filterBlocks.forEach(filterBlock => {
                // Handle button/link clicks
                filterBlock.addEventListener('click', (event) => {
                    const filterElement = event.target.closest('[data-listing-element="filter"][data-listing-filter-type="taxonomy"]');
                    if (!filterElement || !(filterElement.tagName === 'BUTTON' || filterElement.tagName === 'A')) return;

                    event.preventDefault();
                    
                    const taxonomy = filterElement.dataset.listingTaxonomy;
                    if (!taxonomy) return;
                    
                    // Remove active class from other buttons in the same taxonomy group within THIS filter block
                    filterBlock.querySelectorAll(`[data-listing-filter-type="taxonomy"][data-listing-taxonomy="${taxonomy}"]`)
                        .forEach(btn => btn.classList.remove('is-active'));
                    
                    // Toggle active state
                    filterElement.classList.toggle('is-active');
                    
                    // Create a new filter data object for this taxonomy
                    let newTerms = [];
                    
                    // If active and not "all", add this term
                    if (filterElement.classList.contains('is-active') && filterElement.dataset.listingTermId !== 'all') {
                        const termId = parseInt(filterElement.dataset.listingTermId, 10);
                        if (!isNaN(termId) && termId !== 0) {
                            newTerms = [termId];
                        }
                    }
                    
                    // Update the stored filter data for this taxonomy
                    if (newTerms.length > 0) {
                        listingFilterData.taxonomies[taxonomy] = newTerms;
                    } else {
                        delete listingFilterData.taxonomies[taxonomy];
                    }
                    
                    // Trigger filter change
                    this.handleFilterChange(contentBlock, { ...listingFilterData });
                });

                // Handle select changes
                filterBlock.addEventListener('change', (event) => {
                    const filterElement = event.target.closest('[data-listing-element="filter"][data-listing-filter-type="taxonomy"]');
                    if (!filterElement || filterElement.tagName !== 'SELECT') return;

                    const taxonomy = filterElement.dataset.listingTaxonomy;
                    if (!taxonomy) return;
                    
                    const termId = parseInt(filterElement.value, 10);
                    
                    // Update the stored filter data
                    if (!isNaN(termId) && termId !== 0) {
                        listingFilterData.taxonomies[taxonomy] = [termId];
                    } else {
                        delete listingFilterData.taxonomies[taxonomy];
                    }
                    
                    this.handleFilterChange(contentBlock, { ...listingFilterData });
                });

                // Set active state for buttons and selected for selects
                const urlParams = new URLSearchParams(window.location.search);

                // Get ALL filter elements in this filter block, not just the first one
                const filterElements = filterBlock.querySelectorAll('[data-listing-element="filter"][data-listing-filter-type="taxonomy"]');

                // Process each filter element
                filterElements.forEach(filterElement => {
                    const taxonomy = filterElement.dataset.listingTaxonomy;
                    if (!taxonomy) return;
                    
                    const urlTaxonomies = urlParams.get(`fid${contentBlock.dataset.listingId}_${taxonomy}`);
                    if (!urlTaxonomies) return;
                    
                    const urlTaxonomiesArray = urlTaxonomies.split(',');
                    if (!urlTaxonomiesArray || urlTaxonomiesArray.length === 0) return;
                    
                    urlTaxonomiesArray.forEach(termId => {
                        if (filterElement.tagName === 'SELECT') {
                            // For select elements, set the value if it matches
                            if (!isNaN(termId) && termId !== 0 && filterElement.querySelector(`option[value="${termId}"]`)) {
                                filterElement.value = termId;
                                filterElement.dispatchEvent(new Event('change'));
                            }
                        } else if ((filterElement.tagName === 'BUTTON' || filterElement.tagName === 'A') && 
                                filterElement.dataset.listingTermId === termId) {
                            // For button/link elements, add active class if term ID matches
                            filterElement.classList.add('is-active');
                        }
                    });
                });

            });
            
        });
    }

    initializeSearch() {
        document.querySelectorAll('.wp-block-hoyry-listing').forEach((wrapper) => {
            const contentBlock = wrapper.querySelector('.wp-block-hoyry-listing-content');
            
            if (!contentBlock || !contentBlock.dataset || !contentBlock.dataset.listingId) {
                return;
            }
            
            const listingId = contentBlock.dataset.listingId;
            
            // Find all search blocks within this listing
            const searchBlocks = wrapper.querySelectorAll('.wp-block-hoyry-listing-search');
            
            if (searchBlocks.length === 0) {
                return;
            }
    
            // Get the existing filter data from the filter initialization if available
            let listingFilterData = contentBlock.hoyryFilterData || {
                taxonomies: {},
                search: ''
            };

            // If filter initialization hasn't run yet, create our own object
            if (!contentBlock.hoyryFilterData) {
                contentBlock.hoyryFilterData = listingFilterData;
            }

            // Initialize search from URL or input values
            const urlParams = new URLSearchParams(window.location.search);
            const searchParam = urlParams.get(`fid${listingId}_search`);
            
            if (searchParam) {
                listingFilterData.search = searchParam;
                
                // Update all search inputs with the URL value
                searchBlocks.forEach(searchBlock => {
                    const searchInput = searchBlock.querySelector('[data-listing-filter-type="search"]');
                    if (searchInput) {
                        searchInput.value = searchParam;
                    }
                });
            } else {
                // Check all search inputs for an initial value
                for (const searchBlock of searchBlocks) {
                    const searchInput = searchBlock.querySelector('[data-listing-filter-type="search"]');
                    if (searchInput && searchInput.value) {
                        listingFilterData.search = searchInput.value.trim();
                        break;
                    }
                }
            }

            // Process all search blocks
            searchBlocks.forEach(searchBlock => {
                // Handle search input with debounce
                const searchInput = searchBlock.querySelector('[data-listing-filter-type="search"]');
                if (!searchInput) return;

                const debouncedSearch = debounce(() => {
                    // Update the search value in the listing's filter data
                    const newSearchValue = searchInput.value ? searchInput.value.trim() : '';
                    
                    // Update all other search inputs to match this value (for UI consistency)
                    searchBlocks.forEach(otherBlock => {
                        const otherInput = otherBlock.querySelector('[data-listing-filter-type="search"]');
                        if (otherInput && otherInput !== searchInput) {
                            otherInput.value = newSearchValue;
                        }
                    });
                    
                    // Update the search data
                    listingFilterData.search = newSearchValue;
                    
                    // Trigger the filter change
                    this.handleFilterChange(contentBlock, { ...listingFilterData });
                }, 200);

                // Add input event listener
                searchInput.addEventListener('input', debouncedSearch);
                
                // Also handle form submission (if within a form)
                const form = searchInput.closest('form');
                if (form) {
                    form.addEventListener('submit', (e) => {
                        e.preventDefault();
                        debouncedSearch.flush(); // Immediately execute the debounced function
                    });
                }

                // Add click event listener to the search icon
                const searchIconWrapper = searchBlock.querySelector('.hoyry-listing-search-form-icon-wrapper');
                if (searchIconWrapper) {
                    searchIconWrapper.style.cursor = 'pointer'; // Make the icon appear clickable
                    searchIconWrapper.addEventListener('click', () => {
                        debouncedSearch.flush(); // Immediately execute the debounced function
                    });
                }

            });
        });
    }
    
    async handleLoadMore(event) {
        const button = event.target.closest('.hoyry-listing-load-more');
        if (this.isLoading || !button) return;
        
        const buttonTextContainer = button.querySelector('.hoyry-listing-load-more-text');
        const blockContainer = button.closest('.wp-block-hoyry-listing-content');
        const postsContainer = blockContainer?.querySelector('.hoyry-listing-items');
        
        const originalText = buttonTextContainer ? buttonTextContainer.textContent : '';
        
        if (!blockContainer || !postsContainer) {
            console.error('Required containers not found');
            return;
        }

        let queryData;
        try {
            queryData = JSON.parse(blockContainer.dataset.query);
        } catch (error) {
            console.error('Failed to parse query data:', error);
            return;
        }

        try {
            this.isLoading = true;
            if (buttonTextContainer) {
                buttonTextContainer.textContent = TRANSLATIONS.loading;
            }
            button.disabled = true;
            
            const currentPage = parseInt(button.dataset.page) || 1;
            const nextPage = currentPage + 1;

            // Create the request URL with query parameters
            const requestUrl = new URL('/wp-json/hoyry-listing/v2/load_more', window.location.origin);
            requestUrl.searchParams.set('page', nextPage.toString());
            requestUrl.searchParams.set('lang', blockContainer.dataset.lang);
            requestUrl.searchParams.set('attributes', JSON.stringify({
                query: queryData,
                template: 'grid',
                perPage: queryData.perPage || 10
            }));

            // Add current filter parameters from URL if they exist
            const currentUrlParams = new URLSearchParams(window.location.search);
            for (const [key, value] of currentUrlParams.entries()) {
                if (key.startsWith('fid')) {
                    requestUrl.searchParams.set(key, value);
                }
            }

            const response = await fetch(requestUrl, {
                headers: {
                    'X-WP-Nonce': hoyryListing.nonce,
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.data?.length > 0) {
                // Create new posts HTML
                const newPostsHtml = data.data.map(post => {
                    const templateName = queryData.template || 'grid';
                    const template = this.templateCache.get(templateName);
                    if (!template) return '';

                    const postElement = document.createElement('div');
                    postElement.innerHTML = template.postContainer;
                    const postNode = postElement.firstElementChild;

                    // Update post content
                    this.updatePostContent(postNode, post, template.placeholderImage);

                    return postNode.outerHTML;
                }).join('');

                // Append new posts instead of replacing
                postsContainer.insertAdjacentHTML('beforeend', newPostsHtml);

                // Update button state with the next page number and check against max_pages
                button.dataset.page = nextPage.toString();
                
                // Update button visibility
                if (nextPage >= data.max_pages) {
                    const loadMoreContainer = button.closest('.hoyry-listing-load-more-container');
                    if (loadMoreContainer) {
                        loadMoreContainer.style.display = 'none';
                        button.disabled = true;
                    }
                } else {
                    button.disabled = false;
                    if (buttonTextContainer) {
                        buttonTextContainer.textContent = originalText;
                    }
                }
            } else {
                // No more posts to load, hide the button
                const loadMoreContainer = button.closest('.hoyry-listing-load-more-container');
                if (loadMoreContainer) {
                    loadMoreContainer.style.display = 'none';
                    button.disabled = true;
                }
            }
        } catch (error) {
            console.error('Load more error:', error);
            if (buttonTextContainer) {
                buttonTextContainer.textContent = TRANSLATIONS.errorLoading;
            }
            button.disabled = true;
        } finally {
            this.isLoading = false;
        }
    }
    
    // Add method to prefetch and cache template
    async prefetchTemplate(templateName) {
        if (this.templateCache.has(templateName)) {
            return;
        }

        try {
            // First try to get template from DOM using data-template-name
            const existingTemplate = document.querySelector(`[data-template-name="${templateName}"]`);
            
            if (existingTemplate) {
                const existingPost = existingTemplate.querySelector('.hoyry-listing-item');
                
                // Look for a dedicated template element first
                const dedicatedTemplate = existingTemplate.querySelector('template[data-placeholder]');
                let placeholderContent = null;
                
                if (dedicatedTemplate) {
                    // Get placeholder content from the template element
                    const placeholderElement = dedicatedTemplate.content.querySelector('.hoyry-listing-item-image.placeholder');
                    placeholderContent = placeholderElement?.querySelector('svg, img')?.outerHTML || null;
                } else {
                    // Fallback to looking for placeholder in the existing post
                    const placeholderElement = existingTemplate.querySelector('.hoyry-listing-item-image.placeholder');
                    placeholderContent = placeholderElement?.querySelector('svg, img')?.outerHTML || null;
                    
                    // If no placeholder found, create a default one
                    if (!placeholderContent) {
                        placeholderContent = `
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>`;
                    }
                }

                if (existingPost) {
                    // Capture custom elements template
                    const customTemplate = this.captureCustomElementsTemplate(existingPost);
                    
                    this.templateCache.set(templateName, {
                        container: existingTemplate.outerHTML,
                        postContainer: existingPost.outerHTML,
                        placeholderImage: placeholderContent,
                        customElements: customTemplate ? customTemplate.customElements : {}
                    });
                    return;
                }
            }

            // If no DOM template, try server
            const response = await fetch(`/wp-json/hoyry-listing/v2/get_template_structure?template=${encodeURIComponent(templateName)}`);
            
            if (!response.ok) {
                throw new Error(`Server returned ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            if (!data.success || !data.template) {
                throw new Error('Invalid template data received from server');
            }

            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = data.template.trim();

            const postTemplate = tempContainer.querySelector('.hoyry-listing-item');
            if (!postTemplate) {
                throw new Error('Template is missing article element');
            }

            // Get either SVG or IMG element inside the placeholder
            const placeholderContent = postTemplate.querySelector('.hoyry-listing-item-image.placeholder svg, .hoyry-listing-item-image.placeholder img')?.outerHTML || null;

            this.templateCache.set(templateName, {
                container: tempContainer.innerHTML,
                postContainer: postTemplate.outerHTML,
                placeholderImage: placeholderContent
            });
        } catch (error) {
            console.error(`Error prefetching template '${templateName}':`, error);
            throw error;
        }
    }
    
    initializeLoadMore() {
        document.querySelectorAll('.hoyry-listing-load-more').forEach(button => {
            // Remove any existing listeners first
            button.removeEventListener('click', this.boundHandleLoadMore);
            // Add new listener
            button.addEventListener('click', this.boundHandleLoadMore);
        });
    }
    
    /**
     * Updates post content with new data
     * @param {HTMLElement} postNode - The post element to update
     * @param {Object} post - The post data
     */
    updatePostContent(postNode, post, placeholderImage) {
        if (!postNode || !hoyryListing.elementMapping) return;
        
        const mapping = hoyryListing.elementMapping;
        
        // Handle standard elements first
        Object.entries(mapping).forEach(([key, config]) => {
            if (key === 'taxonomies') {
                this.updateTaxonomies(postNode, post.taxonomies_terms, config, post.id);
                return;
            }

            if (key === 'article') {
                if (config.attributes) {
                    Object.entries(config.attributes).forEach(([attr, value]) => {
                        postNode.setAttribute(attr, post[value]);
                    });
                }
                return;
            }
            if (key === 'image') {

                // First, find ANY image container in the post node
                let imageContainer = postNode.querySelector('[data-listing-element="image-container"]');
                
                // If that fails, try alternative selectors
                if (!imageContainer) {
                    imageContainer = postNode.querySelector('.hoyry-listing-item-image');
                }
                
                // If still no container, try any element that might contain an image
                if (!imageContainer) {
                    imageContainer = postNode.querySelector('figure') || 
                                     postNode.querySelector('.image') || 
                                     postNode.querySelector('.post-thumbnail');
                }
                
                if (!imageContainer) {
                    console.error("Could not find image container for post:", post.id);
                    return;
                }
                
                // Clear the container first
                imageContainer.innerHTML = '';
                
                if (post.image) {
                    // Remove placeholder class if it exists
                    imageContainer.classList.remove('placeholder');
                    
                    // Create a new img element
                    const img = document.createElement('img');
                    
                    // Set image attributes
                    img.src = post.image;
                    img.alt = post.image_alt || post.title || '';
                    img.loading = 'lazy';
                    img.setAttribute('data-listing-element', 'image');
                    img.decoding = 'async';
                    img.className = post.image_class || 'attachment-large size-large';
                    
                    if (post.image_srcset) {
                        img.srcset = post.image_srcset;
                    }
                    
                    if (post.image_width) {
                        img.width = post.image_width;
                    }
                    
                    if (post.image_height) {
                        img.height = post.image_height;
                    }
                    
                    // Add to the container
                    imageContainer.appendChild(img);
                    
                } else if (placeholderImage) {
                    // Apply placeholder
                    imageContainer.classList.add('placeholder');
                    imageContainer.innerHTML = placeholderImage;
                }
                
                return;
            }

            const element = postNode.querySelector(config.selector);
            if (!element) return;

            // Handle attributes
            if (config.attributes) {
                Object.entries(config.attributes).forEach(([attr, value]) => {
                    element.setAttribute(attr, post[value]);
                });
            }

            // Handle content
            if (config.content) {
                const content = post[config.content] || '';
                if (config.decode) {
                    element.textContent = content ? decodeEntities(content) : '';
                } else if (config.html) {
                    element.innerHTML = content || '';
                } else {
                    element.textContent = content || '';
                }
            }
        });
        
        // Now handle custom elements from the template
        const templateName = post.template || 'grid';
        const template = this.templateCache.get(templateName);
        
        if (template && template.customElements) {
            Object.entries(template.customElements).forEach(([elementType, elementConfig]) => {
                // Check if we have data for this element
                if (post[elementType] !== undefined) {
                    // Find or create the element
                    let customElement = postNode.querySelector(`[data-listing-element="${elementType}"]`);
                    
                    // If element doesn't exist, create it
                    if (!customElement) {
                        // Create a temporary container
                        const tempContainer = document.createElement('div');
                        tempContainer.innerHTML = elementConfig.html;
                        customElement = tempContainer.firstElementChild;
                        
                        // Find the parent element to attach to
                        let parentElement;
                        if (elementConfig.parentType === 'content') {
                            parentElement = postNode.querySelector('.hoyry-listing-item-content');
                        } else {
                            parentElement = postNode.querySelector(`[data-listing-element="${elementConfig.parentType}"]`);
                        }
                        
                        // If we found a parent, append the custom element
                        if (parentElement) {
                            parentElement.appendChild(customElement);
                        }
                    }
                    
                    // Set the content if we have an element
                    if (customElement) {
                        customElement.textContent = post[elementType];
                    }
                }
            });
        }
    }
    
    updateTaxonomies(postNode, taxonomies, config, postId) {
        if (!postNode || !taxonomies || !config) {
            return;
        }

        // Find the taxonomies container
        const container = postNode.querySelector('[data-listing-element="taxonomies"]');
        if (!container) {
            return;
        }

        // Get original taxonomy group class
        const originalTaxonomyGroupClass = container.querySelector('[data-taxonomy-group]').classList.value;
        const originalTaxonomyTermClass = container.querySelector('[data-listing-element="term"]').classList.value;

        // Clear existing taxonomy groups
        container.querySelectorAll('[data-taxonomy-group]').forEach(el => el.remove());

        // Process each taxonomy
        Object.entries(taxonomies).forEach(([taxonomy, terms]) => {
            // Skip if terms is false or empty
            if (!terms || terms.length === 0) {
                return;
            }

            // Ensure terms is an array
            const termsArray = Array.isArray(terms) ? terms : [terms];
            if (termsArray.length === 0) {
                return;
            }

            // Create taxonomy group
            const group = document.createElement('div');
            group.setAttribute('data-listing-element', 'taxonomy-group');
            group.setAttribute('data-taxonomy-group', taxonomy);
            group.classList.add(originalTaxonomyGroupClass);

            // Add terms to group
            termsArray.forEach(term => {
                if (!term || typeof term !== 'object') {
                    return;
                }

                const termEl = document.createElement('span');
                termEl.setAttribute('data-listing-element', 'term');
                termEl.setAttribute('data-taxonomy', taxonomy);
                termEl.setAttribute('data-term-id', term.term_id);
                termEl.classList.add(originalTaxonomyTermClass);
                termEl.textContent = term.name;
                group.appendChild(termEl);
            });

            // Only append group if it has terms
            if (group.children.length > 0) {
                container.appendChild(group);
            }
        });
    }
    
    initializeModal() {
        
        // Only initialize once
        if (this.modalInitialized) return;
        this.modalInitialized = true;
        
        // Create modal if it doesn't exist
        if (!document.getElementById('hoyry-listing-modal')) {
            const modal = document.createElement('dialog');
            modal.id = 'hoyry-listing-modal';
            modal.className = 'hoyry-listing-modal';
            modal.innerHTML = `
                <button class="modal-close" aria-label="${TRANSLATIONS.close}">
                    <span class="modal-close-text">${TRANSLATIONS.close}</span>
                    <span class="modal-close-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none"><path d="M24 8L8 24M8 8L24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                    </span>
                </button>
                <div class="modal-content-wrapper">
                    <div class="modal-content-container">
                        <h2 class="modal-title"></h2>
                        <div class="modal-content"></div>
                    </div>
                </div>
            `;
            document.body.appendChild(modal);
            
            // Close button handler
            modal.querySelector('.modal-close').addEventListener('click', () => {
                modal.close();
            });
    
            // Click outside to close
            modal.addEventListener('click', (e) => {
                if (e.target === modal) modal.close();
            });
        }
        
        // Add click handler to articles
        jQuery(document).on('click', '.hoyry-listing-item[data-open-in-popup="true"]', (e) => {
            const article = e.target.closest('.hoyry-listing-item[data-open-in-popup="true"]');
            if (article) {
                e.preventDefault();
                this.openPostInModal(article);
            }
        });
        // Add click handler to featured item
        jQuery(document).on('click', '.hoyry-featured-item[data-open-in-popup="true"]', (e) => {
            const featuredItem = e.target.closest('.hoyry-featured-item[data-open-in-popup="true"]');
            if (featuredItem) {
                e.preventDefault();
                this.openPostInModal(featuredItem);
            }
        });
    }
    
    async openPostInModal(articleElement) {
        const postId = articleElement.dataset.postId;
        const modal = document.getElementById('hoyry-listing-modal');
        const titleEl = modal.querySelector('.modal-title');
        const contentEl = modal.querySelector('.modal-content');
        
        // Show loading state
        titleEl.textContent = TRANSLATIONS.loading;
        contentEl.innerHTML = `<div class="loading">${TRANSLATIONS.loading}</div>`;
        modal.showModal();
        
        try {
            const response = await fetch(`/wp-json/hoyry-listing/v2/get_post_content?post_id=${postId}`, {
                headers: {
                    'X-WP-Nonce': hoyryListing.nonce,
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin'
            });
            
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const result = await response.json();
            
            if (result.success) {
                titleEl.textContent = result.data.title;
                contentEl.innerHTML = `
                    <div class="modal-post-content is-layout-constrained">
                        ${result.data.content}
                    </div>
                `;
            } else {
                throw new Error('Failed to load post content');
            }
            
        } catch (error) {
            console.error('Error loading post:', error);
            contentEl.innerHTML = TRANSLATIONS.errorLoading;
        }
    }

    /**
     * Creates a template from an existing post to capture custom elements added via hooks
     * @param {HTMLElement} existingPost - An existing post element with all custom elements
     * @returns {Object} Template object with HTML structure
     */
    captureCustomElementsTemplate(existingPost) {
        if (!existingPost) return null;
        
        // Clone the post to avoid modifying the original
        const template = existingPost.cloneNode(true);
        
        // Find all custom elements that have data-listing-element attributes
        // but are not part of the standard mapping
        const standardSelectors = Object.values(hoyryListing.elementMapping)
            .filter(item => item.selector)
            .map(item => item.selector);
        
        // Get all elements with data-listing-element attribute
        const allElements = template.querySelectorAll('[data-listing-element]');
        
        // Create a map of custom elements and their parent-child relationships
        const customElements = {};
        
        Array.from(allElements).forEach(element => {
            const elementType = element.getAttribute('data-listing-element');
            const isStandard = standardSelectors.some(selector => 
                element.matches(selector)
            );
            
            // If this is not a standard element, record its structure
            if (!isStandard) {
                // Get the closest standard parent element
                let parent = element.parentElement;
                let parentType = null;
                
                while (parent && !parentType) {
                    if (parent.hasAttribute('data-listing-element')) {
                        parentType = parent.getAttribute('data-listing-element');
                        // Check if this is a standard element
                        if (standardSelectors.some(selector => parent.matches(selector))) {
                            break;
                        }
                    }
                    parent = parent.parentElement;
                }
                
                // Store the element info
                customElements[elementType] = {
                    parentType: parentType || 'content', // Default to content area
                    html: element.outerHTML,
                    selector: `[data-listing-element="${elementType}"]`
                };
                
                // Clear the content but keep the structure
                const contentElement = element.querySelector(':first-child');
                if (contentElement) {
                    contentElement.textContent = '';
                } else {
                    element.textContent = '';
                }
            }
        });
        
        return {
            customElements: customElements,
            template: template
        };
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const handler = new QueryBlockHandler();
    // Check if we're not in admin area before initializing
    if (!document.body.classList.contains('wp-admin')) {
        // Initialize in specific order after DOM is loaded
        handler.initializeBlocks().then(() => {
            handler.initializeLoadMore();
            // Initialize filters first, then search, as search may depend on filter data
            handler.initializeFilters();
            handler.initializeSearch();
        });
    }
});

// Re-initialize on dynamic content changes
document.addEventListener('contentChanged', () => {
    const handler = QueryBlockHandler.instance;
    // Check if we're not in admin area before initializing
    if (!document.body.classList.contains('wp-admin')) {
        if (handler) {
            handler.initializeBlocks().then(() => {
                handler.initializeLoadMore();
                // Initialize filters first, then search, as search may depend on filter data
                handler.initializeFilters();
                handler.initializeSearch();
            });
        }
    }
});